import * as React from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import type { Option as AsyncSelectOption } from "@/components/forms/AsyncSelect";
import stellar from "@/services/stellar";
import type { PurchaseFormValues, PurchasePayload } from "../types";

const PurchaseSchema = z.object({
	id: z.string().optional(),
	supplier: z
		.string({ required_error: "Selecione um fornecedor" })
		.min(1, "Selecione um fornecedor"),
	material: z
		.string({ required_error: "Selecione um material" })
		.min(1, "Selecione um material"),
	origin: z
		.string({ required_error: "Selecione uma origem" })
		.min(1, "Selecione uma origem"),
	price: z.coerce.number().min(0, "Preço inválido"),
	quantity: z.coerce.number(),
	observation: z
		.string()
		.optional()
		.transform((value) => value?.trim() ?? ""),
	link: z
		.string()
		.optional()
		.transform((value) => value?.trim() ?? ""),
	request: z
		.string()
		.optional()
		.transform((value) => (value ? value : "")),
});

const DEFAULT_VALUES: PurchaseFormValues = {
	id: undefined,
	supplier: "",
	material: "",
	origin: "",
	price: 1,
	quantity: 1,
	observation: "",
	link: "",
	request: "",
};

type UsePurchasesFormParams = {
	defaultValues: PurchaseFormValues;
};

type UsePurchasesFormResult = {
	form: UseFormReturn<PurchaseFormValues>;
	resetToInitialValues: () => void;
	querySuppliers: (query: string) => Promise<AsyncSelectOption[]>;
	queryMaterials: (query: string) => Promise<AsyncSelectOption[]>;
};

const usePurchasesForm = ({
	defaultValues,
}: UsePurchasesFormParams): UsePurchasesFormResult => {
	const form = useForm<PurchaseFormValues>({
		resolver: zodResolver(PurchaseSchema),
		defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
	});

	React.useEffect(() => {
		form.reset({ ...DEFAULT_VALUES, ...defaultValues });
	}, [defaultValues, form]);

	const querySuppliers = React.useCallback(
		async (query: string): Promise<AsyncSelectOption[]> => {
			const trimmed = query.trim();

			if (trimmed.length < 2) {
				return [];
			}

			try {
				const { suppliers } = (await stellar.action("searchSuppliers", {
					query: trimmed,
				})) as { suppliers: Array<{ id: string; name: string }> };

				return suppliers.map((supplier) => ({
					value: supplier.id,
					label: supplier.name,
				}));
			} catch {
				return [];
			}
		},
		[],
	);

	const queryMaterials = React.useCallback(
		async (query: string): Promise<AsyncSelectOption[]> => {
			const trimmed = query.trim();

			if (trimmed.length < 2) {
				return [];
			}

			try {
				const { materials } = (await stellar.action("searchMaterials", {
					query: trimmed,
				})) as {
					materials: Array<{
						_id: string;
						brand: string;
						model: string;
						description: string;
					}>;
				};

				return materials.map((material) => ({
					value: material._id,
					label: `${material.brand}, ${material.model}, ${material.description}`,
				}));
			} catch {
				return [];
			}
		},
		[],
	);

	const resetToInitialValues = React.useCallback(() => {
		form.reset({ ...DEFAULT_VALUES, ...defaultValues });
	}, [form, defaultValues]);

	return {
		form,
		resetToInitialValues,
		querySuppliers,
		queryMaterials,
	};
};

const buildPurchasePayload = (values: PurchaseFormValues): PurchasePayload => ({
	supplier: values.supplier,
	material: values.material,
	origin: values.origin as PurchasePayload["origin"],
	price: values.price,
	quantity: values.quantity,
	observation: values.observation ?? "",
	link: values.link ?? "",
	request: values.request ? values.request : null,
});

export { DEFAULT_VALUES, usePurchasesForm, buildPurchasePayload };
